/**
 * 決済用iframeモーダルの状態管理と読み込み完了検出を行うカスタムフック
 *
 * GMO-PG決済時の3DS認証はiframe内で実施されるため、iframeの開閉や
 * 読み込み完了を適切に検出する必要があります。このフックは以下を提供します：
 *
 * - iframeモーダルの開閉状態管理
 * - iframe読み込み完了の検出（Promise ベース）
 * - iframeのcontentWindow参照の管理
 *
 * 使用例：
 * ```tsx
 * const iframeHandler = usePaymentIframe();
 *
 * // コンポーネント内でModalをレンダリング
 * <PaymentIframeModal
 *   isOpen={iframeHandler.isOpen}
 *   onClose={iframeHandler.closeIframe}
 *   initialUrl={iframeHandler.initialUrl}
 *   onIframeLoad={iframeHandler.handleIframeLoad}
 * />
 *
 * // iframeを開いて読み込み完了を待つ（必ずawaitする）
 * const iframeWindow = await iframeHandler.openIframe(url);
 * // この時点でModal側のonLoadがトリガーされ、Promiseがresolveされている
 * // iframeWindowを使って処理...
 *
 * // 処理完了後にiframeを閉じる
 * iframeHandler.closeIframe();
 * ```
 *
 * 動作の流れ：
 * 1. openIframe(url)　を呼び出すと　Promise　を返す
 * 2. Modalがレンダリングされ、iframe　が読み込まれる
 * 3. iframe onLoadイベントで　handleIframeLoad　が呼ばれる
 * 4. handleIframeLoad　が openIframe　で待っている 　Promiseをresolveし、contentWindowを返す
 * 5. await openIframe(url)　が完了して　iframeWindow　が取得できる
 */
import { useCallback, useMemo, useRef, useState } from 'react';

export const usePaymentIframe = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [initialUrl, setInitialUrl] = useState<string | undefined>(undefined);
  const loadResolveRef = useRef<((window: Window) => void) | null>(null);

  /**
   * iframe読み込み完了時に呼ばれるコールバック
   * contentWindowを取得してopenIframeのPromiseをresolveする
   */
  const handleIframeLoad = useCallback((iframe: HTMLIFrameElement) => {
    const window = iframe.contentWindow;
    if (window && loadResolveRef.current) {
      // openIframe の Promise を resolve
      loadResolveRef.current(window);
      loadResolveRef.current = null;
    }
    return window;
  }, []);

  /**
   * iframeモーダルを開いて、読み込み完了を待つ
   * @param url - iframeで表示するURL
   * @return iframe読み込み完了後にcontentWindowを返すPromise
   */
  const openIframe = useCallback((url: string): Promise<Window> => {
    setInitialUrl(url);
    setIsOpen(true);
    // iframeの読み込み完了を待つPromiseを返す
    return new Promise<Window>((resolve) => {
      loadResolveRef.current = resolve;
    });
  }, []);

  /**
   * iframeモーダルを閉じる
   * 決済処理完了後に呼び出す
   */
  const closeIframe = useCallback(() => {
    setIsOpen(false);
    loadResolveRef.current = null;
  }, []);

  return useMemo(
    () => ({
      isOpen,
      initialUrl,
      handleIframeLoad,
      openIframe,
      closeIframe,
    }),
    [isOpen, initialUrl, handleIframeLoad, openIframe, closeIframe],
  );
};
